import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useClaims } from "@/hooks/use-claims";
import { useToast } from "@/hooks/use-toast";
import { apiClient } from "@/lib/api-client";
import { fileToBase64, normalizeDateToYYYYMMDD } from "@/lib/utils";
import { getErrorMessage } from "@/lib/errors";
import { usePdfUtils } from "@/hooks/use-pdf-utils";
import { Loader2, Upload, FileText, X } from "lucide-react";
import { Document, ClaimMetadata } from "@/types/claims";
import { CostConfirmationDialog, CostEstimate } from "./CostConfirmationDialog";
import MetadataReviewModal from "../MetadataReviewModal";
import { useComparisons } from "@/hooks/use-comparisons";
import { cn } from "@/lib/utils";
import { ClaimPipelineSelect } from "./ClaimPipelineSelect";
import { usePaymentMethodGate } from "@/hooks/use-subscription-guard";
import { PaywallModal } from "@/components/subscription/PaywallModal";
import type { CreateDocumentRequestMimeTypeEnum } from "@/api/generated/client/models/CreateDocumentRequest";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UploadEstimatesFormProps {
    onSuccess: (claimId: number, comparisonId?: number) => void;
}

export function UploadEstimatesForm({ onSuccess }: UploadEstimatesFormProps) {
    const { createClaimAsync, updateClaimAsync } = useClaims();
    const { toast } = useToast();
    const [pipelineId, setPipelineId] = useState<number | undefined>();
    const { countPages } = usePdfUtils();

    // File State
    const [insuranceFile, setInsuranceFile] = useState<File | null>(null);
    const [contractorFile, setContractorFile] = useState<File | null>(null);
    const [uploadedDocuments, setUploadedDocuments] = useState<Document[]>([]);

    // Page count state
    const [insurancePages, setInsurancePages] = useState<number | null>(null);
    const [contractorPages, setContractorPages] = useState<number | null>(null);

    // Processing State
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState<string>("");

    // Cost State
    const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
    const [showCostConfirm, setShowCostConfirm] = useState(false);

    // Metadata State
    const [currentClaimId, setCurrentClaimId] = useState<number | null>(null);
    const [extractedMetadata, setExtractedMetadata] = useState<{
        insurance: ClaimMetadata;
        contractor: ClaimMetadata;
    } | null>(null);
    const [showReviewModal, setShowReviewModal] = useState(false);

    // Comparison Hook (needs claimId)
    const { createComparisonAsync } = useComparisons(currentClaimId || undefined);

    // Paywall: free users must have a payment method to run comparisons
    const { shouldPaywall } = usePaymentMethodGate();
    const [paywallOpen, setPaywallOpen] = useState(false);

    const [extractionError, setExtractionError] = useState<{
        message: string;
        partialResults: Array<{ success: boolean, data?: ClaimMetadata, error?: string }>;
        docs: Document[];
    } | null>(null);

    const [uploadError, setUploadError] = useState<{
        message: string;
        details?: string;
    } | null>(null);

    const handleErrorCancel = () => {
        setExtractionError(null);
        setUploadedDocuments([]);
        setCostEstimate(null); // Reset to allow re-upload
        setIsProcessing(false);
        setProcessingStatus("");
    };

    const handleUploadErrorClose = () => {
        setUploadError(null);
        setIsProcessing(false);
        setProcessingStatus("");
    };

    const handleErrorContinue = () => {
        if (!extractionError) return;

        const { partialResults, docs } = extractionError;
        setExtractionError(null);

        // Continue processing with whatever results we have
        const results: { insurance: ClaimMetadata, contractor: ClaimMetadata } = {
            insurance: {},
            contractor: {}
        };

        docs.forEach((doc, idx) => {
            const result = partialResults[idx];
            if (result && result.success && result.data) {
                if (doc.kind === "insurer") results.insurance = result.data;
                else if (doc.kind === "contractor") results.contractor = result.data;
            }
        });

        setExtractedMetadata(results);
        setShowReviewModal(true);
        setIsProcessing(false);
        setProcessingStatus("");
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>, type: "insurance" | "contractor") => {
        let file: File | undefined;

        if ('target' in e && (e.target as HTMLInputElement).files) {
            file = (e.target as HTMLInputElement).files?.[0];
        } else if ('dataTransfer' in e) {
            file = e.dataTransfer.files?.[0];
        }

        if (!file) return;

        // Validate file size (50MB limit)
        const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > MAX_FILE_SIZE) {
            toast({
                title: "File too large",
                description: `The selected file exceeds the maximum size of 50MB. Please select a smaller file.`,
                variant: "destructive"
            });
            return;
        }

        if (type === "insurance") {
            setInsuranceFile(file);
            setInsurancePages(null); // Clear old page count
        } else {
            setContractorFile(file);
            setContractorPages(null); // Clear old page count
        }

        // Reset estimate if files change
        setCostEstimate(null);
        setUploadedDocuments([]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, type: "insurance" | "contractor") => {
        e.preventDefault();
        e.stopPropagation();
        handleFileUpload(e, type);
    };

    const initializeDraftClaim = async (): Promise<number> => {
        if (currentClaimId) return currentClaimId;

        const uniqueId = crypto.randomUUID();
        const draft = await createClaimAsync({
            claimNumber: `DRAFT-${uniqueId}`,
            insuredName: "Draft Claim",
            pipelineId,
            claimStatusTemplateId: null, // Backend auto-assigns first status template by display order
            description: "Draft claim created from document upload"
        });

        if (!draft?.id) throw new Error("Failed to create draft claim");
        setCurrentClaimId(draft.id);
        return draft.id;
    };

    const handlePayloadTooLarge = (error: any): Error => {
        const errorMessage = error?.response?.data?.error?.message ||
            error?.body?.error?.message ||
            'File size exceeds the maximum allowed limit';
        const maxSizeMB = error?.response?.data?.error?.details?.maxSizeMB ||
            error?.body?.error?.details?.maxSizeMB ||
            '50MB';

        const err = new Error(`${errorMessage}. Maximum file size: ${maxSizeMB}`);
        (err as any).isFileSizeError = true;
        (err as any).maxSize = maxSizeMB;
        return err;
    };

    const uploadDocument = async (file: File, kind: "insurer" | "contractor", claimId: number): Promise<Document> => {
        const base64 = await fileToBase64(file);

        const payload = {
            createDocumentRequest: {
                filename: file.name,
                mimeType: (file.type || "application/pdf") as CreateDocumentRequestMimeTypeEnum,
                sizeBytes: file.size,
                kind: kind,
                fileContent: base64,
                claimId: claimId
            }
        };

        try {
            return await apiClient.documents.createDocument(payload);
        } catch (error: any) {
            // Check for payload too large error (413 status)
            const status = error?.response?.status || error?.status;
            const errorCode = error?.response?.data?.error?.code || error?.body?.error?.code;

            if (status === 413 || errorCode === 'PAYLOAD_TOO_LARGE') {
                throw handlePayloadTooLarge(error);
            }

            // Re-throw other errors
            throw error;
        }
    };

    const estimateCost = async () => {
        if (!insuranceFile && !contractorFile) {
            toast({
                title: "No files selected",
                description: "Please select at least one PDF file to estimate costs.",
                variant: "destructive"
            });
            return;
        }

        setIsProcessing(true);
        setProcessingStatus("Analyzing PDF files...");

        try {
            // Count pages for each file
            const pageCounts: number[] = [];

            if (insuranceFile) {
                setProcessingStatus("Analyzing insurance estimate...");
                const pages = await countPages(insuranceFile);
                setInsurancePages(pages);
                pageCounts.push(pages);
            }

            if (contractorFile) {
                setProcessingStatus("Analyzing contractor estimate...");
                const pages = await countPages(contractorFile);
                setContractorPages(pages);
                pageCounts.push(pages);
            }

            setProcessingStatus("Calculating cost estimate...");

            // Call API with the request wrapper object
            const estimate = await apiClient.billing.estimateBillingFromPageCounts({
                estimateBillingFromPageCountsRequest: { pageCounts }
            });

            // Use page counts from API response - fail if missing
            if (!estimate.documents[0] || estimate.documents[0].pages === undefined) {
                throw new Error('API response missing insurance document page count');
            }
            if (!estimate.documents[1] || estimate.documents[1].pages === undefined) {
                throw new Error('API response missing contractor document page count');
            }
            const insurancePages = estimate.documents[0].pages;
            const contractorPages = estimate.documents[1].pages;

            if (!estimate.tierCode) {
                throw new Error('API response missing tier code');
            }

            setCostEstimate({
                totalCredits: estimate.totalCredits,
                totalCostCents: estimate.totalCostCents,
                tierCode: estimate.tierCode,
                totalPages: estimate.totalPages,
                insurancePages,
                contractorPages,
                costPerCreditCents: estimate.costPerCreditCents
            });

            setShowCostConfirm(true);

        } catch (error) {
            console.error("Estimation Error:", error);
            toast({
                title: "Estimation failed",
                description: getErrorMessage(error),
                variant: "destructive"
            });
        } finally {
            setIsProcessing(false);
            setProcessingStatus("");
        }
    };

    const processFiles = async () => {
        setShowCostConfirm(false);
        setIsProcessing(true);

        try {
            setProcessingStatus("Creating claim...");
            const claimId = await initializeDraftClaim();

            setProcessingStatus("Uploading documents...");
            const docs: Document[] = [];
            let insuranceDoc: Document | null = null;
            let contractorDoc: Document | null = null;

            if (insuranceFile) {
                insuranceDoc = await uploadDocument(insuranceFile, "insurer", claimId);
                docs.push(insuranceDoc);
            }
            if (contractorFile) {
                contractorDoc = await uploadDocument(contractorFile, "contractor", claimId);
                docs.push(contractorDoc);
            }
            setUploadedDocuments(docs);

            setProcessingStatus("Extracting metadata from documents...");

            // Trigger metadata extraction for both documents in parallel (fire and forget)
            const metadataPromises = docs.map(async (doc) => {
                try {
                    await apiClient.documents.extractDocumentMetadata({ documentId: doc.id });
                } catch (error) {
                    console.error(`Failed to trigger metadata extraction for doc ${doc.id}:`, error);
                }
            });

            // Don't wait for metadata extraction to complete
            Promise.all(metadataPromises).catch(err =>
                console.error("Metadata extraction trigger failed:", err)
            );

            // Poll for metadata to show in review modal
            setProcessingStatus("Waiting for metadata extraction...");
            const results: { insurance: ClaimMetadata, contractor: ClaimMetadata } = {
                insurance: {},
                contractor: {}
            };

            // Poll with timeout - if it takes too long, proceed with empty metadata.
            // Allow extra time for OCR processing: force-OCR on large scanned PDFs
            // can take 90-120s, plus retry extraction adds ~5-10s on top.
            // The poll exits early on 'completed', 'metadata_completed', or 'failed'
            // so fast documents (~10s) aren't penalized by the generous timeout.
            const METADATA_TIMEOUT = 300_000; // 5 minutes
            const startTime = Date.now();

            const pollMetadata = async (doc: Document): Promise<{ success: boolean, data?: ClaimMetadata, error?: string }> => {
                const POLL_INTERVAL_MS = 2000;
                let shownOcrMessage = false;
                while (Date.now() - startTime < METADATA_TIMEOUT) {
                    // Update status message once after 15s to indicate OCR may be running
                    if (!shownOcrMessage && Date.now() - startTime > 15000) {
                        shownOcrMessage = true;
                        setProcessingStatus("Applying OCR to scanned document — this may take a moment...");
                    }

                    try {
                        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
                        const statusResponse = await apiClient.documents.getExtractionStatus({ documentId: doc.id });

                        if (statusResponse.status === 'completed' || statusResponse.status === 'metadata_completed') {
                            const extractedData = await apiClient.documents.getDocumentExtractedData({ documentId: doc.id });
                            return { success: true, data: extractedData.metadata };
                        }

                        if (statusResponse.status === 'failed') {
                            console.error(`Metadata extraction failed for doc ${doc.id}: ${statusResponse.error}`);
                            return { success: false, error: statusResponse.error || "Extraction failed" };
                        }
                    } catch (error) {
                        console.error(`Error polling metadata for doc ${doc.id}:`, error);
                    }
                }
                console.warn(`Metadata extraction timeout for doc ${doc.id}`);
                return { success: false, error: "Timeout occurred" };
            };

            const metadataResults = await Promise.all(
                docs.map(doc => pollMetadata(doc))
            );

            // Check for errors
            const errors = metadataResults.filter(r => !r.success && r.error);
            if (errors.length > 0) {
                const firstError = errors[0].error || "Unknown error occurred";

                // Set error state to show dialog
                setExtractionError({
                    message: firstError,
                    partialResults: metadataResults,
                    docs: docs
                });

                // Do NOT set isProcessing to false here, keep it true until user decides?
                // Visual preference: maybe stop spinner but keep overlay?
                // Actually the Dialog will appear on top.
                return;
            }

            docs.forEach((doc, idx) => {
                const result = metadataResults[idx];
                if (result.success && result.data) {
                    if (doc.kind === "insurer") results.insurance = result.data;
                    else if (doc.kind === "contractor") results.contractor = result.data;
                }
            });

            setExtractedMetadata(results);
            setShowReviewModal(true);
            setIsProcessing(false);
            setProcessingStatus("");

        } catch (error: any) {
            console.error("Processing Error:", error);

            // Check if this is a file size error
            if (error?.isFileSizeError) {
                setUploadError({
                    message: getErrorMessage(error),
                    details: error?.maxSize ? `Maximum allowed file size is ${error.maxSize}` : undefined
                });
            } else {
                toast({
                    title: "Processing failed",
                    description: getErrorMessage(error),
                    variant: "destructive"
                });
            }

            setIsProcessing(false);
            setProcessingStatus("");
        }
    };

    const handleMetadataConfirm = async (finalMetadata: ClaimMetadata) => {
        if (!currentClaimId) return;

        setIsProcessing(true);
        try {
            setProcessingStatus("Updating claim with metadata...");
            await updateClaimAsync({
                claimId: currentClaimId,
                updateClaimRequest: {
                    insuredName: finalMetadata.insuredName || finalMetadata.claimNumber || "Unknown Insured",
                    propertyAddress: finalMetadata.insuredAddress,
                    insuredPhone: finalMetadata.insuredPhone,
                    insuredEmail: finalMetadata.insuredEmail,
                    claimNumber: finalMetadata.claimNumber || "PENDING",
                    policyNumber: finalMetadata.policyNumber,
                    dateOfLoss: normalizeDateToYYYYMMDD(finalMetadata.dateOfLoss),
                    lossType: finalMetadata.typeOfLoss as any,
                    insuranceCarrier: finalMetadata.estimatorCompany || undefined,
                    description: finalMetadata.insuredName + " - " + finalMetadata.claimNumber,
                },
                isDraftFinalization: true, // Signal that this update finalizes a draft claim
            });

            // Start Document-Based Comparison
            const insurerDoc = uploadedDocuments.find(d => d.kind === 'insurer');
            const contractorDoc = uploadedDocuments.find(d => d.kind === 'contractor');

            if (insurerDoc && contractorDoc) {
                // Comparisons are pay-as-you-go: block if no payment method on file.
                // usePaymentMethodGate only reports shouldPaywall once the subscription
                // status is definitively known (not loading/errored) so paid users aren't
                // falsely blocked; the backend still enforces regardless.
                // The claim is already created, so keep this component mounted (do NOT
                // call onSuccess, which would navigate away and unmount the PaywallModal
                // before the user can see it). The user can add a card from the modal and
                // run the comparison later from the claim's Comparisons module.
                if (shouldPaywall) {
                    toast({
                        title: "Claim created",
                        description: "Add a payment method to run the comparison.",
                    });
                    setPaywallOpen(true);
                    return;
                }
                try {
                    setProcessingStatus("Starting comparison workflow...");

                    // Create Comparison with DOCUMENT IDs (extraction happens in workflow)
                    const comp = await createComparisonAsync({
                        insurerDocumentId: insurerDoc.id,
                        contractorDocumentId: contractorDoc.id
                    });

                    toast({ title: "Comparison started successfully" });

                    // Navigate to claim page (where the progress card will be shown)
                    onSuccess(currentClaimId, comp.id);
                } catch (e) {
                    console.error("Failed to start comparison", e);
                    toast({
                        title: "Comparison start failed",
                        description: "Could not start comparison. Please try again.",
                        variant: "destructive"
                    });
                    // Still navigate to claim
                    onSuccess(currentClaimId);
                }
            } else {
                toast({ title: "Claim updated with extracted data" });
                onSuccess(currentClaimId);
            }
        } catch (error) {
            console.error("Failed to update claim with metadata:", error);
            console.error("Metadata being sent:", {
                insuredName: finalMetadata.insuredName || finalMetadata.claimNumber || "Unknown Insured",
                propertyAddress: finalMetadata.insuredAddress,
                insuredPhone: finalMetadata.insuredPhone,
                insuredEmail: finalMetadata.insuredEmail,
                claimNumber: finalMetadata.claimNumber || "PENDING",
                policyNumber: finalMetadata.policyNumber,
                dateOfLoss: finalMetadata.dateOfLoss || undefined,
                lossType: finalMetadata.typeOfLoss,
                insuranceCarrier: finalMetadata.estimatorCompany || undefined,
                description: finalMetadata.insuredName + " - " + finalMetadata.claimNumber,
            });
            toast({
                title: "Failed to update claim",
                description: getErrorMessage(error),
                variant: "destructive"
            });
        } finally {
            setIsProcessing(false);
            setProcessingStatus("");
        }
    };

    return (
        <div className="space-y-4">
            <PaywallModal
                open={paywallOpen}
                onOpenChange={setPaywallOpen}
                feature="comparison"
            />
            <ClaimPipelineSelect value={pipelineId} onChange={setPipelineId} disabled={isProcessing} />
            <div className="grid gap-4">
                {/* Insurance Upload */}
                <div
                    className={cn(
                        "border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors cursor-pointer",
                        insuranceFile ? "bg-muted/30" : ""
                    )}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDrop={(e) => handleDrop(e, "insurance")}
                >
                    <Label htmlFor="insurance-upload" className="cursor-pointer block">
                        <div className="flex flex-col items-center gap-2">
                            {insuranceFile ? (
                                <>
                                    <FileText className="h-8 w-8 text-blue-500" />
                                    <span className="font-medium text-blue-600">{insuranceFile.name}</span>
                                    {insurancePages && (
                                        <span className="text-sm text-muted-foreground">{insurancePages} pages</span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Upload className="h-8 w-8 text-muted-foreground" />
                                    <span className="text-muted-foreground">Upload Insurance Estimate (PDF)</span>
                                    <p className="text-xs text-muted-foreground mt-1">Drag file here or click to browse</p>
                                </>
                            )}
                        </div>
                    </Label>
                    <Input
                        id="insurance-upload"
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, "insurance")}
                        disabled={isProcessing}
                    />
                    {insuranceFile && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setInsuranceFile(null);
                                setInsurancePages(null);
                                setCostEstimate(null);
                                setUploadedDocuments([]);
                            }}
                            className="mt-2 text-destructive"
                            disabled={isProcessing}
                        >
                            <X className="h-4 w-4 mr-1" /> Remove
                        </Button>
                    )}
                </div>

                {/* Contractor Upload */}
                <div
                    className={cn(
                        "border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors cursor-pointer",
                        contractorFile ? "bg-muted/30" : ""
                    )}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDrop={(e) => handleDrop(e, "contractor")}
                >
                    <Label htmlFor="contractor-upload" className="cursor-pointer block">
                        <div className="flex flex-col items-center gap-2">
                            {contractorFile ? (
                                <>
                                    <FileText className="h-8 w-8 text-green-500" />
                                    <span className="font-medium text-green-600">{contractorFile.name}</span>
                                    {contractorPages && (
                                        <span className="text-sm text-muted-foreground">{contractorPages} pages</span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Upload className="h-8 w-8 text-muted-foreground" />
                                    <span className="text-muted-foreground">Upload Contractor Estimate (PDF)</span>
                                    <p className="text-xs text-muted-foreground mt-1">Drag file here or click to browse</p>
                                </>
                            )}
                        </div>
                    </Label>
                    <Input
                        id="contractor-upload"
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, "contractor")}
                        disabled={isProcessing}
                    />
                    {contractorFile && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setContractorFile(null);
                                setContractorPages(null);
                                setCostEstimate(null);
                                setUploadedDocuments([]);
                            }}
                            className="mt-2 text-destructive"
                            disabled={isProcessing}
                        >
                            <X className="h-4 w-4 mr-1" /> Remove
                        </Button>
                    )}
                </div>

                <Button
                    onClick={estimateCost}
                    disabled={(!insuranceFile && !contractorFile) || isProcessing}
                    className="w-full"
                >
                    {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {isProcessing ? processingStatus : "Estimate Cost"}
                </Button>
            </div>

            {/* PROCESSING OVERLAY */}
            {isProcessing && !extractionError && !uploadError && (
                <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                        <Loader2 className="h-12 w-12 animate-spin text-primary" />
                        <h3 className="text-lg font-semibold">{processingStatus}</h3>
                        <p className="text-muted-foreground">Please wait while we process your documents.</p>
                    </div>
                </div>
            )}

            <CostConfirmationDialog
                open={showCostConfirm}
                onOpenChange={setShowCostConfirm}
                estimate={costEstimate}
                onConfirm={processFiles}
            />

            <AlertDialog open={!!uploadError} onOpenChange={(open) => !open && handleUploadErrorClose()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>File Upload Error</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-muted-foreground space-y-4">
                            <p className="text-destructive font-medium">
                                {uploadError?.message}
                            </p>
                            {uploadError?.details && (
                                <p className="text-sm text-muted-foreground">
                                    {uploadError.details}
                                </p>
                            )}
                            <p className="text-sm">
                                Please select a smaller PDF to upload.
                            </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={handleUploadErrorClose}>
                            OK
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!extractionError} onOpenChange={(open) => !open && handleErrorCancel()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Extraction Issue Detected</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-muted-foreground space-y-4">
                            <p>We encountered an issue while extracting metadata from your document(s):</p>
                            <p className="font-mono text-xs bg-muted p-2 rounded text-destructive break-words">
                                {extractionError?.message}
                            </p>
                            <p>
                                Canceling is recommended, as you will not be billed for this failed extraction.
                                You can review the file requirements (e.g. readable text, not scanned images) and try again.
                            </p>
                            <p>
                                If you wish to proceed, you can manually enter the missing data.
                            </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleErrorCancel}>Cancel & Fix File</AlertDialogCancel>
                        <AlertDialogAction onClick={handleErrorContinue}>Continue Anyway</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {extractedMetadata && (
                <MetadataReviewModal
                    open={showReviewModal}
                    onOpenChange={setShowReviewModal}
                    insuranceMetadata={extractedMetadata.insurance}
                    contractorMetadata={extractedMetadata.contractor}
                    onConfirm={handleMetadataConfirm}
                    isProcessing={isProcessing}
                />
            )}
        </div>
    );
}
